import { Component, OnInit, ElementRef, ViewChild } from '@angular/core';
import { debounceTime } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ToastNotificationComponent } from '../toast-notification/toast-notification.component';
import { Trucker } from '../models/Trucker';
import { TruckerData } from "../services/truckerData.service";
import { TruckerItem } from '../models/TruckerItem';
import { TruckerContact } from '../models/TruckerContact';
import { TruckerSearchParameters } from '../models/TruckerSearchParameters';
import { ListDataService } from '../services/list-data.service';
import { PhoneValidationService } from '../services/phone-validation.service';
import { PhoneValidationResponse } from '../models/PhoneValidation';


@Component({
    selector: 'truckers',
    templateUrl: 'truckers.component.html',
    styleUrls: ['truckers.component.css'],
    standalone: false
})
export class TruckersComponent implements OnInit {
    @ViewChild('contactNameInput') ContactNameField: ElementRef;
    @ViewChild('truckerNameInput') TruckerNameField: ElementRef;
    @ViewChild('toastNotification') toastNotificationComponent: ToastNotificationComponent;
    constructor(
        private _truckerData: TruckerData,
        private _listDataService: ListDataService,
        private _phoneValidationService: PhoneValidationService
    ) { }

    errorMessage: string = '';
    trucker: Trucker = new Trucker();
    truckerList: TruckerItem[] = [];
    truckerSearchParams: TruckerSearchParameters = new TruckerSearchParameters();
    selectedTrucker: number = 0;
    selectedTruckerContact: TruckerContact = new TruckerContact();
    tableHeight: number = 0;
    originalTrucker: Trucker = new Trucker();
    originalTruckerContact: TruckerContact = new TruckerContact();
    hasTruckerChangedSubject = new Subject<void>();
    private _hasTruckerChanged: boolean;
    showEmailWarning: boolean = false;
    isValidatingPhone1: boolean = false;
    isValidatingPhone2: boolean = false;
    isPhone1Invalid: boolean = false;
    isPhone2Invalid: boolean = false;
    phone1ErrorMessage: string = '';
    phone2ErrorMessage: string = '';
    favoriteTruckerIds: number[] = [];

    get isPhone1Landline(): boolean {
        return !!this.selectedTruckerContact?.phone1 && !this.selectedTruckerContact?.isPhone1TextEnabled;
    }

    get isPhone2Landline(): boolean {
        return !!this.selectedTruckerContact?.phone2 && !this.selectedTruckerContact?.isPhone2TextEnabled;
    }

    searchTruckers() {
        this.selectedTrucker = -99;
        this.selectedTruckerContact = new TruckerContact();
        this._truckerData.searchTruckers(this.truckerSearchParams).subscribe({
            next: data => {
                this.truckerList = data.sort((a, b) => {
                    const aFav = this.favoriteTruckerIds.includes(a.customerId) ? 1 : 0;
                    const bFav = this.favoriteTruckerIds.includes(b.customerId) ? 1 : 0;
                    if (bFav !== aFav) return bFav - aFav;
                    return a.customerName.localeCompare(b.customerName);
                });
            },
            error: err => this.errorMessage = err
        });
    }

    selectTrucker(trucker: TruckerItem): void {
        this.showEmailWarning = false;
        this.isPhone1Invalid = false;
        this.isPhone2Invalid = false;
        this.phone1ErrorMessage = '';
        this.phone2ErrorMessage = '';
        this.truckerList.forEach(t => t.isSelected = false);
        trucker.isSelected = true;
        this.selectedTrucker = trucker.customerId;
        this.getTruckerById(trucker.customerId);
    }

    selectTruckerContact(contact: TruckerContact): void {
        this.showEmailWarning = false;
        this.isPhone1Invalid = false;
        this.isPhone2Invalid = false;
        this.phone1ErrorMessage = '';
        this.phone2ErrorMessage = '';
        this.selectedTruckerContact = contact;
        this.updateHasTruckerChanged();
    }

    getTruckerById(customerId: number) {
        this._truckerData.getTruckerById(customerId).subscribe({
            next: data => {
                this.trucker = data;
                this.originalTrucker = this.deepClone(this.trucker);

                const list = this.trucker.contactList ?? [];
                const primary = list.find(c => c.isPrimary) || null;

                this.selectedTruckerContact = primary ?? (list[0] ?? new TruckerContact());

                this.updateHasTruckerChanged();
            },
            error: err => this.errorMessage = err
        })
    }

    getComplianceClass(): string {
        if (!this.trucker.hasCompliance) {
            return 'text-warning';
        }
        if (this.trucker.compliance.isCompliant) {
            return 'text-success';
        }
        else {
            return 'text-danger';
        }
    }

    newContact(): void {
        this.showEmailWarning = false;
        this.trucker.contactList.push({
            contactId: 0,
            contactName: "New Contact",
            fax: "",
            email: "",
            notes: "",
            phone1: "",
            phone2: "",
            isPhone1TextEnabled: false,
            isPhone2TextEnabled: false,
            isPrimary: false,
            hasChanged: true,
            isPhone1Default: false,
            isPhone2Default: false,
            isPhone1Selected: false,
            isPhone2Selected: false,
            isEmailDefault: false,
            phone1ValidationStatus: 'pending',
            phone2ValidationStatus: 'pending',
            phone1LastValidatedDate: null,
            phone2LastValidatedDate: null
        });

        this.selectedTruckerContact = this.trucker.contactList[this.trucker.contactList.length - 1]
        this.ContactNameField.nativeElement.focus();
        setTimeout(() => {
            this.ContactNameField.nativeElement.select();
        }, 50);
    }

    deleteContact(contactListId: number): void {
        const index = this.trucker.contactList.findIndex(x => x.contactId === contactListId);
        if (index > -1) {
            const wasPrimary = this.trucker.contactList[index].isPrimary === true;

            this.trucker.contactList.splice(index, 1);

            if (this.trucker.contactList.length > 0) {
                this.selectedTruckerContact = this.trucker.contactList[0];
                if (wasPrimary) {
                    this.trucker.contactList.forEach(c => c.isPrimary = false);
                }
            } else {
                this.selectedTruckerContact = new TruckerContact();
            }

            this._hasTruckerChanged = true;
        }
    }

    revertContactChanges(): void {
        this.showEmailWarning = false;
        this.isPhone1Invalid = false;
        this.isPhone2Invalid = false;
        this.phone1ErrorMessage = '';
        this.phone2ErrorMessage = '';
        this.getTruckerById(this.selectedTrucker);
        this.updateHasTruckerChanged();
    }

    primaryContactChanged(): void {
        this.trucker.contactList.forEach(x => x.isPrimary = false);
        this.selectedTruckerContact.isPrimary = true;
        this.autoEnablePrimaryRequirements(this.selectedTruckerContact);
        this.updateHasTruckerChanged();
    }

    saveTrucker() {
        this.showEmailWarning = false;

        // Blur any focused phone input to trigger validation before save
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement && (activeElement.id === 'ContactPhone1' || activeElement.id === 'ContactPhone2')) {
            activeElement.blur();
        }

        // Wait for any pending validations to complete before saving
        if (this.isValidatingPhone1 || this.isValidatingPhone2) {
            setTimeout(() => this.saveTrucker(), 100);
            return;
        }

        if (this.isPhone1Invalid || this.isPhone2Invalid) {
            this.toastNotificationComponent.showToast("Phone number is not valid", 5000, "error");
            return;
        }

        this._truckerData.saveTrucker(this.trucker).subscribe({
            next: result => {
                if (result) {
                    this.toastNotificationComponent.showToast("Changes saved", 3000, "success");
                    this.selectedTruckerContact.hasChanged = false;
                    this.originalTrucker = this.deepClone(this.trucker);
                    this.updateHasTruckerChanged();
                }
                else {
                    this.toastNotificationComponent.showToast("Failed to save, please try again", 120000, "error");
                }
            },
            error: err => this.errorMessage = err
        })
    }

    checkParametersChange(): void {
        this.truckerSearchParams.hasChanged = true;
    }

    hasTruckerChanged(): boolean {
        if (
            this.trucker.comments !== this.originalTrucker.comments ||
            this.trucker.isRogerEnabled !== this.originalTrucker.isRogerEnabled ||
            this.trucker.isActive !== this.originalTrucker.isActive ||
            this.trucker.contactList.length !== this.originalTrucker.contactList.length 
        ) {
            return true;
        }

        for (let i = 0; i < this.trucker.contactList.length; i++) {
            const currentContact = this.trucker.contactList[i];
            const originalContact = this.originalTrucker.contactList[i];

            if (
                currentContact.contactName !== originalContact.contactName ||
                currentContact.phone1 !== originalContact.phone1 ||
                currentContact.isPhone1TextEnabled !== originalContact.isPhone1TextEnabled ||
                currentContact.phone2 !== originalContact.phone2 ||
                currentContact.isPhone2TextEnabled !== originalContact.isPhone2TextEnabled ||
                currentContact.fax !== originalContact.fax ||
                currentContact.email !== originalContact.email ||
                currentContact.notes !== originalContact.notes ||
                currentContact.isPrimary !== originalContact.isPrimary ||
                currentContact.isPhone1Default !== originalContact.isPhone1Default || 
                currentContact.isPhone2Default !== originalContact.isPhone2Default ||
                currentContact.isEmailDefault !== originalContact.isEmailDefault
            ) {
                return true;
            }
        }

        return false;
    }

    updateHasTruckerChanged() {
        this.hasTruckerChangedSubject.next();
    }

    get hasTruckerChangedStatus(): boolean {
        return this._hasTruckerChanged;
    }

    deepClone<T>(obj: T): T {
        return JSON.parse(JSON.stringify(obj));
    }

    isPrimaryPersisted(item: TruckerContact): boolean {
        if (!this.originalTrucker?.contactList?.length) return false;
        const match = this.originalTrucker.contactList.find(c =>(item.contactId && c.contactId === item.contactId) || (!item.contactId && !c.contactId && c.contactName === item.contactName));
        return !!match?.isPrimary;
    }

    isEmailDefaultEnabled(): boolean {
        return this.selectedTruckerContact.email &&
            this.selectedTruckerContact.email.trim().length > 0;
    }

    onPhone1TextableChanged(): void {
        if (!this.selectedTruckerContact.isPhone1TextEnabled) {
            this.selectedTruckerContact.isPhone1Default = false;
        }
        this.updateHasTruckerChanged();
    }

    onPhone2TextableChanged(): void {
        if (!this.selectedTruckerContact.isPhone2TextEnabled) {
            this.selectedTruckerContact.isPhone2Default = false;
        }
        this.updateHasTruckerChanged();
    }

    onPhone1DefaultChanged(): void {
        this.updateHasTruckerChanged();
    }

    onPhone2DefaultChanged(): void {
        this.updateHasTruckerChanged();
    }

    onPhone1Changed(): void {
        if (!this.selectedTruckerContact.phone1 || this.selectedTruckerContact.phone1.trim().length === 0) {
            this.selectedTruckerContact.isPhone1TextEnabled = false;
            this.selectedTruckerContact.isPhone1Default = false;
            this.isPhone1Invalid = false;
            this.phone1ErrorMessage = '';
        } else {
            this.validatePhone1();
        }
        this.updateHasTruckerChanged();
    }

    onPhone2Changed(): void {
        if (!this.selectedTruckerContact.phone2 || this.selectedTruckerContact.phone2.trim().length === 0) {
            this.selectedTruckerContact.isPhone2TextEnabled = false;
            this.selectedTruckerContact.isPhone2Default = false;
            this.isPhone2Invalid = false;
            this.phone2ErrorMessage = '';
        } else {
            this.validatePhone2();
        }
        this.updateHasTruckerChanged();
    }

    private validatePhone1(): void {
        const phone = this.selectedTruckerContact.phone1;
        const digits = phone?.replace(/\D/g, '') || '';
        if (digits.length < 10) {
            return;
        }

        this.isValidatingPhone1 = true;
        this._phoneValidationService.validatePhoneNumber(phone).subscribe({
            next: (result: PhoneValidationResponse) => {
                this.isValidatingPhone1 = false;
                if (result.isValid && (result.isMobile || result.isTollFree)) {
                    this.selectedTruckerContact.isPhone1TextEnabled = true;
                    this.isPhone1Invalid = false;
                    this.phone1ErrorMessage = '';
                    this.selectedTruckerContact.phone1ValidationStatus = 'validated';
                    this.selectedTruckerContact.phone1LastValidatedDate = new Date();
                } else if (result.isLandline) {
                    this.selectedTruckerContact.isPhone1TextEnabled = false;
                    this.selectedTruckerContact.isPhone1Default = false;
                    this.isPhone1Invalid = false;
                    this.phone1ErrorMessage = '';
                    this.selectedTruckerContact.phone1ValidationStatus = 'validated';
                    this.selectedTruckerContact.phone1LastValidatedDate = new Date();
                } else if (!result.isValid) {
                    this.selectedTruckerContact.isPhone1TextEnabled = false;
                    this.selectedTruckerContact.isPhone1Default = false;
                    this.isPhone1Invalid = true;
                    this.phone1ErrorMessage = 'Phone number is not valid.';
                    this.selectedTruckerContact.phone1 = '';
                    this.selectedTruckerContact.phone1ValidationStatus = 'failed';
                    this.selectedTruckerContact.phone1LastValidatedDate = new Date();
                }
                this.updateHasTruckerChanged();
            },
            error: () => {
                this.isValidatingPhone1 = false;
            }
        });
    }

    private validatePhone2(): void {
        const phone = this.selectedTruckerContact.phone2;
        const digits = phone?.replace(/\D/g, '') || '';
        if (digits.length < 10) {
            return;
        }

        this.isValidatingPhone2 = true;
        this._phoneValidationService.validatePhoneNumber(phone).subscribe({
            next: (result: PhoneValidationResponse) => {
                this.isValidatingPhone2 = false;
                if (result.isValid && (result.isMobile || result.isTollFree)) {
                    this.selectedTruckerContact.isPhone2TextEnabled = true;
                    this.isPhone2Invalid = false;
                    this.phone2ErrorMessage = '';
                    this.selectedTruckerContact.phone2ValidationStatus = 'validated';
                    this.selectedTruckerContact.phone2LastValidatedDate = new Date();
                } else if (result.isLandline) {
                    this.selectedTruckerContact.isPhone2TextEnabled = false;
                    this.selectedTruckerContact.isPhone2Default = false;
                    this.isPhone2Invalid = false;
                    this.phone2ErrorMessage = '';
                    this.selectedTruckerContact.phone2ValidationStatus = 'validated';
                    this.selectedTruckerContact.phone2LastValidatedDate = new Date();
                } else if (!result.isValid) {
                    this.selectedTruckerContact.isPhone2TextEnabled = false;
                    this.selectedTruckerContact.isPhone2Default = false;
                    this.isPhone2Invalid = true;
                    this.phone2ErrorMessage = 'Phone number is not valid.';
                    this.selectedTruckerContact.phone2 = '';
                    this.selectedTruckerContact.phone2ValidationStatus = 'failed';
                    this.selectedTruckerContact.phone2LastValidatedDate = new Date();
                }
                this.updateHasTruckerChanged();
            },
            error: () => {
                this.isValidatingPhone2 = false;
            }
        });
    }

    onEmailChanged(): void {
        if (!this.selectedTruckerContact.email || this.selectedTruckerContact.email.trim().length === 0) {
            this.selectedTruckerContact.isEmailDefault = false;
            this.selectedTruckerContact.isPrimary = false;
        } else {
            this.showEmailWarning = false;
        }
        this.updateHasTruckerChanged();
    }

    onEmailDefaultChanged(): void {
        if (!this.selectedTruckerContact.isEmailDefault) {
            this.selectedTruckerContact.isPrimary = false;
        }
        this.updateHasTruckerChanged();
    }

    onPrimaryToggleForSelected(): void {
        const selected = this.selectedTruckerContact;
        if (!selected) return;

        if (selected.isPrimary) {
            (this.trucker?.contactList ?? []).forEach(c => {
                if (c !== selected) {
                    c.isPrimary = false;
                }
            });

            this.autoEnablePrimaryRequirements(selected);
        }

        this.updateHasTruckerChanged();
    }

    onPrimaryAreaClick(): void {
        if (!this.selectedTruckerContact.email || this.selectedTruckerContact.email.trim().length === 0) {
            this.showEmailWarning = true;

            // Focus on email field to guide user
            const emailInput = document.getElementById('ContactEmail') as HTMLInputElement;
            if (emailInput) {
                emailInput.focus();
            }
        }
    }

    autoEnablePrimaryRequirements(contact: TruckerContact): void {
        if (contact.email && contact.email.trim().length > 0) {
            contact.isEmailDefault = true;
        }
    }

    clearSearchParameters(): void {
        this.truckerSearchParams = new TruckerSearchParameters();
        this.trucker = null;
        this.selectedTrucker = null;
        this.selectedTruckerContact = new TruckerContact();
        this.truckerList.forEach(t => t.isSelected = false);

        this.TruckerNameField.nativeElement.focus();
        setTimeout(() => {
            this.TruckerNameField.nativeElement.select();
        }, 50);
    }

    onEnter(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            this.searchTruckers();
        }
    }

    setTableHeight() {
        let screenHeight = window.innerHeight;
        let screenModifier = .85; //Default for 24" monitors (952px)

        if (screenHeight < 850) { //For laptop displays (838px)
            screenModifier = .83;
        }

        return this.tableHeight = screenHeight * screenModifier;
    }

    loadFavoriteTruckers(): void {
        this._listDataService.getFavoriteTruckers().subscribe({
            next: data => {
                this.favoriteTruckerIds = data.map(t => t.customerId);
            },
            error: err => this.errorMessage = err
        });
    }

    isFavoriteTrucker(trucker: TruckerItem): boolean {
        return this.favoriteTruckerIds.includes(trucker.customerId);
    }

    toggleFavoriteTrucker(trucker: TruckerItem, event: Event): void {
        event.stopPropagation();

        const isFavorite = this.isFavoriteTrucker(trucker);

        if (isFavorite) {
            this.favoriteTruckerIds = this.favoriteTruckerIds.filter(id => id !== trucker.customerId);
            this._listDataService.removeFavoriteTrucker(trucker.customerId).subscribe({
                next: result => {
                    if (result) {
                        this.toastNotificationComponent.showToast(`${trucker.customerName} removed from favorites`, 3000, 'success');
                    } else {
                        this.loadFavoriteTruckers();
                        this.toastNotificationComponent.showToast('Failed to update favorites', 3000, 'error');
                    }
                },
                error: err => {
                    this.loadFavoriteTruckers();
                    this.errorMessage = err;
                }
            });
        } else {
            this.favoriteTruckerIds.push(trucker.customerId);
            this._listDataService.addFavoriteTrucker(trucker.customerId).subscribe({
                next: result => {
                    if (result) {
                        this.toastNotificationComponent.showToast(`${trucker.customerName} added to favorites`, 3000, 'success');
                    } else {
                        this.loadFavoriteTruckers();
                        this.toastNotificationComponent.showToast('Failed to update favorites', 3000, 'error');
                    }
                },
                error: err => {
                    this.loadFavoriteTruckers();
                    this.errorMessage = err;
                }
            });
        }
    }

    ngOnInit(): void {
        this.setTableHeight();
        window.addEventListener('resize', () =>
            this.setTableHeight()
        );

        // Subscribe to the subject with debounce
        this.hasTruckerChangedSubject.pipe(debounceTime(300)).subscribe(() => {
            this._hasTruckerChanged = this.hasTruckerChanged();
        });

        this.updateHasTruckerChanged();
        this.loadFavoriteTruckers();
    }
}
